import React from "react";
import PropTypes from "prop-types";
import "./ProgressBar.scss";

const clamp = (n) => Math.max(0, Math.min(100, n));

/**
 * Barra de progreso reutilizable (atom).
 *
 * Se puede usar de dos formas:
 *  - controlada por porcentaje: <ProgressBar value={75} />
 *  - controlada por fracción:   <ProgressBar current={3} total={4} />
 *
 * Props:
 *  - value: number (0-100) — tiene prioridad sobre current/total
 *  - current, total: number — calcula el porcentaje
 *  - size: "sm" | "md" — grosor de la barra (default "md")
 *  - tone: "primary" | "success" — color del relleno (default "primary")
 *  - animated: boolean — anima el ancho con transición (default true)
 *  - ariaLabel: string — etiqueta accesible
 *  - className: string
 */
const ProgressBar = ({
	value,
	current,
	total,
	size = "md",
	tone = "primary",
	animated = true,
	ariaLabel,
	className = "",
}) => {
	const pct =
		value != null
			? clamp(value)
			: total > 0
				? clamp((current / total) * 100)
				: 0;

	return (
		<div
			className={`c-progress c-progress--${size} c-progress--${tone} ${
				animated ? "is-animated" : ""
			} ${className}`.trim()}
			role="progressbar"
			aria-valuenow={Math.round(pct)}
			aria-valuemin={0}
			aria-valuemax={100}
			aria-label={ariaLabel}
		>
			<div className="c-progress__track">
				<div className="c-progress__fill" style={{ width: `${pct}%` }} />
			</div>
		</div>
	);
};

ProgressBar.propTypes = {
	value: PropTypes.number,
	current: PropTypes.number,
	total: PropTypes.number,
	size: PropTypes.oneOf(["sm", "md"]),
	tone: PropTypes.oneOf(["primary", "success"]),
	animated: PropTypes.bool,
	ariaLabel: PropTypes.string,
	className: PropTypes.string,
};

export default ProgressBar;
